import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Typography, Button, Space } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

const PageHeader = ({
    title,
    subtitle,
    backButton,
    actions,
    children,
    maxWidth = 1200,
    showBorder = true
}) => {
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    return (
        <div style={{
            backgroundColor: 'white',
            borderBottom: showBorder ? '1px solid #f0f0f0' : 'none',
            padding: isMobile ? '20px 0' : '32px 0'
        }}>
            <div style={{
                maxWidth,
                margin: '0 auto',
                padding: isMobile ? '0 16px' : '0 24px'
            }}>
                {/* Back Button */}
                {backButton && (
                    <Link to={backButton.to}>
                        <Button
                            type="text"
                            icon={<ArrowLeftOutlined />}
                            size={isMobile ? 'small' : 'middle'}
                            style={{ marginBottom: isMobile ? 12 : 16 }}
                        >
                            {backButton.text}
                        </Button>
                    </Link>
                )}

                {/* Title and Actions Row */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: isMobile ? 'flex-start' : 'flex-start',
                    flexDirection: isMobile && actions ? 'column' : 'row',
                    marginBottom: children ? (isMobile ? 16 : 24) : 0,
                    gap: isMobile ? 12 : 24
                }}>
                    <div style={{ flex: 1, order: isMobile ? 1 : 0 }}>
                        <Title level={1} style={{
                            margin: 0,
                            fontSize: isMobile ? 24 : 32,
                            fontWeight: 700,
                            color: '#1a1a1a',
                            lineHeight: 1.2
                        }}>
                            {title}
                        </Title>
                        {subtitle && (
                            <Text type="secondary" style={{
                                fontSize: isMobile ? 14 : 16,
                                marginTop: isMobile ? 6 : 8,
                                display: 'block',
                                lineHeight: isMobile ? 1.4 : 1.5
                            }}>
                                {subtitle}
                            </Text>
                        )}
                    </div>

                    {/* Actions */}
                    {actions && (
                        <div style={{
                            flexShrink: 0,
                            order: isMobile ? 2 : 0,
                            alignSelf: isMobile ? 'flex-start' : 'flex-start',
                            width: isMobile ? '100%' : 'auto'
                        }}>
                            {Array.isArray(actions) ? (
                                <Space
                                    size={isMobile ? 'small' : 'middle'}
                                    wrap={isMobile}
                                    style={{
                                        justifyContent: isMobile ? 'flex-start' : 'flex-end',
                                        width: isMobile ? '100%' : 'auto'
                                    }}
                                >
                                    {actions.map((action, index) =>
                                        React.cloneElement(action, {
                                            key: index,
                                            size: isMobile ? 'middle' : 'large'
                                        })
                                    )}
                                </Space>
                            ) : (
                                React.cloneElement(actions, {
                                    size: isMobile ? 'middle' : 'large'
                                })
                            )}
                        </div>
                    )}
                </div>

                {/* Custom Content */}
                {children && (
                    <div style={{
                        marginTop: isMobile ? 12 : 0
                    }}>
                        {React.Children.map(children, child => {
                            if (React.isValidElement(child) && child.type?.displayName === 'Search') {
                                return React.cloneElement(child, {
                                    size: isMobile ? 'middle' : 'large',
                                    style: {
                                        ...child.props.style,
                                        maxWidth: isMobile ? '100%' : (child.props.style?.maxWidth || 400)
                                    }
                                });
                            }
                            return child;
                        })}
                    </div>
                )}
            </div>

            {/* CSS for additional responsive styles */}
            <style>{`
                @media (max-width: 480px) {
                    .ant-btn-lg {
                        font-size: 14px !important;
                        height: 36px !important;
                        padding: 4px 12px !important;
                    }

                    .ant-input-search-large .ant-input {
                        font-size: 14px !important;
                        height: 36px !important;
                    }

                    .ant-input-search-large .ant-input-search-button {
                        height: 36px !important;
                    }
                }

                @media (max-width: 576px) {
                    .ant-space-item {
                        margin-bottom: 8px;
                    }
                }
            `}</style>
        </div>
    );
};

export default PageHeader;
